import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DataSelectionContextValue } from './types';

export const DataSelectionContext = createContext<DataSelectionContextValue<any> | undefined>(undefined);

interface DataSelectionProviderProps<T> {
  children: React.ReactNode;
  value?: T[];
  onChange?: (value: T[]) => void;
}

// eslint-disable-next-line @typescript-eslint/comma-dangle
export const DataSelection = <T,>({ children, value = [], onChange = () => {} }: DataSelectionProviderProps<T>) => {
  const prevDataSelection = useRef<T[] | null>(null);

  const [dataSelection, setDataSelection] = useState<T[]>(value);
  const [openItems, setOpenItems] = useState(new Set<T>());

  const updateDataSelection = useCallback((newSelection: T[]) => {
    prevDataSelection.current = newSelection;

    setDataSelection(newSelection);
    onChange(newSelection);
  }, [prevDataSelection.current, onChange]);

  const contextValue = useMemo(() => ({
    dataSelection,
    openItems,
    setDataSelection: updateDataSelection,
    setOpenItems,
  }), [dataSelection, openItems, updateDataSelection]);

  useEffect(() => {
    if (prevDataSelection.current !== value) {
      setDataSelection(value);
    }
  }, [value]);

  return (
    <DataSelectionContext.Provider value={contextValue}>
      {children}
    </DataSelectionContext.Provider>
  );
};

export const useDataSelection = () => {
  const context = useContext(DataSelectionContext);

  if (context === undefined) {
    throw new Error('useDataSelection must be used within a DataSource provider');
  }

  return context;
};
